import React, { useMemo } from "react";
import type { ReactElement } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ErrorState, LoadingState } from "../components/patterns";
import { isAuthStatus, useCurrentUser } from "./queries/auth";

type PortalRole = "Owner" | "Admin" | "Agent" | "Client";

type ProtectedRouteProps = {
  children: ReactElement;
  allowedRoles?: PortalRole[];
};

const ProtectedRoute = ({
  children,
  allowedRoles = [],
}: ProtectedRouteProps) => {
  const location = useLocation();
  const { data: currentUser, error, isPending, refetch } = useCurrentUser();

  const allowed = useMemo(
    () => allowedRoles.map((role) => role.toLowerCase()),
    [allowedRoles],
  );

  if (isPending) {
    return (
      <div className="portal-page">
        <LoadingState size="page" label="Checking access" />
      </div>
    );
  }

  if (isAuthStatus(error, 401)) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (isAuthStatus(error, 403)) {
    return <Navigate to="/403" replace />;
  }
  if (error) {
    return (
      <div className="portal-page">
        <ErrorState
          size="page"
          title="Access check failed"
          description="We could not confirm your access to this page."
          onRetry={() => refetch()}
        />
      </div>
    );
  }
  if (!currentUser) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (currentUser.profile?.password_setup_required) return <Navigate to="/set-password" replace />;

  const userRole = currentUser.role || currentUser.profile?.role;
  const roleOk =
    allowed.length === 0 ||
    allowed.includes(String(userRole || "").toLowerCase());
  return roleOk ? children : <Navigate to="/403" replace />;
};

export default ProtectedRoute;
